import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { z } from "zod";

export type NotificationRecipientJSON = z.infer<typeof NotificationRecipientSchema.NotificationRecipientJSONZ>;
export type NotificationRecipientParams = z.infer<typeof NotificationRecipientSchema.NotificationRecipientZ>;

export default class NotificationRecipientSchema {
  public static readonly NotificationRecipientJSONZ = z.object({
    userId: z.uuid(),
    seenAt: z.iso.datetime({ offset: true }).nullish(),
  });
  public static readonly NotificationRecipientZ = z.object({
    userId: z.instanceof(Guid),
    seenAt: z.instanceof(AHDate).nullable(),
  });

  public static validate(values: unknown): NotificationRecipientParams {
    return parseOrThrow(NotificationRecipientSchema.NotificationRecipientZ, values, {
      logError: true,
      schemaName: "NotificationRecipientSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): NotificationRecipientParams {
    return parseOrThrow(NotificationRecipientSchema.NotificationRecipientZ, values, {
      logError: true,
      schemaName: "NotificationRecipientSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is NotificationRecipientParams {
    try {
      NotificationRecipientSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: NotificationRecipientJSON): NotificationRecipientParams {
    const parsed = parseOrThrow(NotificationRecipientSchema.NotificationRecipientJSONZ, data, {
      logError: true,
      schemaName: "NotificationRecipientSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return NotificationRecipientSchema.codec.decode(parsed);
  }

  public static serialize(recipient: NotificationRecipientParams): NotificationRecipientJSON {
    const parsed = NotificationRecipientSchema.validateJSON(recipient);
    return NotificationRecipientSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    NotificationRecipientSchema.NotificationRecipientJSONZ,
    NotificationRecipientSchema.NotificationRecipientZ,
    {
      decode: (data) => ({
        userId: new Guid(GuidSchema.deserialize(data.userId)),
        seenAt: AHDate.fromJSON(data.seenAt) ?? null,
      }),
      encode: (recipient) => ({
        userId: recipient.userId.toJSON(),
        seenAt: recipient.seenAt?.toJSON(),
      }),
    }
  );
}
